import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, ListGroup, Form, Button, Card } from 'react-bootstrap';
import { FaTrash, FaPlus, FaMinus } from 'react-icons/fa';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { addToCart, removeFromCart, clearCartItems } from '../slices/cartSlice';
import {
  useGetDailyStockDetailsQuery,
  useUpdateDailyStockMutation,
} from '../slices/dailyStocksApiSlice';
import { useGetCustomersQuery } from '../slices/customersApiSlice';
import { toast } from 'react-toastify';

const DailyStocksEditScreen = () => {
  const { id: dailyStockId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { cartItems } = useSelector((state) => state.cart);

  const [mergedOnce, setMergedOnce] = useState(false);

  const [customerSearch, setCustomerSearch] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [note, setNote] = useState('');
  const [dailyStockDate, setDailyStockDate] = useState(new Date().toISOString().split('T')[0]);

  const { data: dailyStockData, isLoading: loadingDailyStock, error: dailyStockError } =
    useGetDailyStockDetailsQuery(dailyStockId);

  const { data: customersData, isLoading: loadingCustomers, error: customersError } =
    useGetCustomersQuery({ keyword: customerSearch });

  const [updateDailyStock, { isLoading: savingDailyStock }] = useUpdateDailyStockMutation();

  // Normalize cart items (convert _id → product)
  const normalizeCart = (items) =>
    items.map((item) => ({
      ...item,
      product: item.product || item._id, // FIXED
    }));

  // Merge logic using productId
  const mergeDailyStockWithCart = (dailyStockItems, cartItems) => {
    const map = new Map();

    normalizeCart(cartItems).forEach((item) => {
      if (!item.product) return;
      map.set(item.product.toString(), { ...item });
    });

    dailyStockItems.forEach((item) => {
      if (!item.product) return;
      map.set(item.product.toString(), { ...item });
    });

    return Array.from(map.values());
  };

  // Load dailyStock data and merge ONCE
  useEffect(() => {
    if (dailyStockData && !mergedOnce) {
      setSelectedCustomer(dailyStockData.customerDetails || null);
      setNote(dailyStockData.note || '');
      setDailyStockDate(
        dailyStockData.date ? dailyStockData.date.split('T')[0] : new Date().toISOString().split('T')[0]
      );

      const merged = mergeDailyStockWithCart(dailyStockData.dailyStockItems, cartItems);

      merged.forEach((item) => {
        dispatch(
          addToCart({
            ...item,
            product: item.product,
            qty: item.qty,
          })
        );
      });

      setMergedOnce(true);
    }
  }, [dailyStockData, mergedOnce]);

  // Filter customers
  useEffect(() => {
    if (customersData?.customers) {
      const filtered = customersData.customers.filter((customer) =>
        customer.name.toLowerCase().includes(customerSearch.toLowerCase())
      );
      setFilteredCustomers(filtered);
    }
  }, [customerSearch, customersData]);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.name);
    setFilteredCustomers([]);
  };

  const increaseQtyHandler = (item) => {
    dispatch(addToCart({ ...item, qty: item.qty + 1 }));
  };

  const decreaseQtyHandler = (item) => {
    if (item.qty > 1) {
      dispatch(addToCart({ ...item, qty: item.qty - 1 }));
    } else {
      toast.error('Quantity cannot be less than 1');
    }
  };

  const removeFromCartHandler = (productId) => {
    dispatch(removeFromCart(productId));
  };

  const updateDailyStockHandler = async () => {
    try {
      if (!selectedCustomer) {
        toast.error('Please select a customer');
        return;
      }

      if (cartItems.length === 0) {
        toast.error('Your cart is empty');
        return;
      }

      const dailyStockItems = normalizeCart(cartItems).map((item) => ({
        product: item.product,
        qty: item.qty,
      }));

      await updateDailyStock({
        dailyStockId,
        dailyStockItems,
        customerDetails: selectedCustomer._id,
        note,
        status: 'Completed',
        date: dailyStockDate,
      }).unwrap();

      toast.success('DailyStock updated successfully');
      dispatch(clearCartItems());
      navigate('/admin/dailyStocklist');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to update dailyStock');
    }
  };

  return (
    <Row>
      <Col md={8}>
        <h1>Edit DailyStock</h1>

        {loadingDailyStock ? (
          <Loader />
        ) : dailyStockError ? (
          <Message variant="danger">{dailyStockError?.data?.message}</Message>
        ) : (
          <>
            {cartItems.length === 0 ? (
              <Message>
                Your cart is empty.{' '}
                <Button onClick={() => navigate('/house/HomeScreen')}>Go Back</Button>
              </Message>
            ) : (
              <ListGroup variant="flush">
                {normalizeCart(cartItems).map((item) => (
                  <ListGroup.Item key={item.product}>
                    <Row>
                      <Col md={2}>
                        {item.images?.length > 0 && (
                          <img
                            src={item.images[0]}
                            alt="Product"
                            style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                          />
                        )}
                      </Col>

                      <Col>{item.name}</Col>

                      <Col md={2}>D{item.price.toFixed(2)}</Col>

                      <Col md={3}>
                        <div className="d-flex align-items-center">
                          <Button variant="light" onClick={() => decreaseQtyHandler(item)}>
                            <FaMinus />
                          </Button>
                          <span className="mx-2">{item.qty}</span>
                          <Button variant="light" onClick={() => increaseQtyHandler(item)}>
                            <FaPlus />
                          </Button>
                        </div>
                      </Col>

                      <Col md={2}>D{(item.qty * item.price).toFixed(2)}</Col>

                      <Col md={1}>
                        <Button variant="light" onClick={() => removeFromCartHandler(item.product)}>
                          <FaTrash />
                        </Button>
                      </Col>
                    </Row>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </>
        )}
      </Col>

      <Col md={4}>
        <Card>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2>Select Customer</h2>

              {loadingCustomers ? (
                <Loader />
              ) : customersError ? (
                <Message variant="danger">{customersError?.data?.message}</Message>
              ) : (
                <>
                  <Form.Group controlId="customerSearch">
                    <Form.Label>Search Customer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Type customer name"
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                    />
                  </Form.Group>

                  {filteredCustomers.length > 0 && (
                    <ListGroup>
                      {filteredCustomers.map((customer) => (
                        <ListGroup.Item
                          key={customer._id}
                          action
                          onClick={() => handleCustomerSelect(customer)}
                        >
                          {customer.name} ({customer.company})
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}

                  {selectedCustomer && (
                    <div className="mt-3">
                      <strong>Selected Customer:</strong>
                      <p>
                        {selectedCustomer.name} ({selectedCustomer.company})<br />
                        Phone: {selectedCustomer.telephone}<br />
                        Address: {selectedCustomer.address}<br />
                        SERIAL: {selectedCustomer.serial}
                      </p>

                      <Form.Group controlId="note">
                        <Form.Label>Note</Form.Label>
                        <Form.Control
                          type="text"
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                        />
                      </Form.Group>

                      <Form.Group controlId="dailyStockDate" className="mt-2">
                        <Form.Label>Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={dailyStockDate}
                          onChange={(e) => setDailyStockDate(e.target.value)}
                        />
                      </Form.Group>
                    </div>
                  )}
                </>
              )}
            </ListGroup.Item>

            <ListGroup.Item>
              <Button
                type="button"
                className="btn-block"
                disabled={savingDailyStock || cartItems.length === 0 || !selectedCustomer}
                onClick={updateDailyStockHandler}
              >
                {savingDailyStock ? 'Processing...' : 'Update DailyStock'}
              </Button>
            </ListGroup.Item>
          </ListGroup>
        </Card>
      </Col>
    </Row>
  );
};

export default DailyStocksEditScreen;
